import { useMemo } from "react";
import type { MyPayEnvironment } from "../../generated/core-api";
import {
  DETECTION_MAX_DISTINCT_PER_COLUMN,
  DETECTION_MAX_VALUES,
  DETECTION_SAMPLE_ROWS,
} from "../config";
import type { CsvPreview } from "../types";
import { useComponentsParameters } from "./useComponentsParameters";

/**
 * Columns that are never the component-identifying column, so they are excluded
 * from value-based detection (they hold employee / scheduling data, not
 * component names).
 */
const NON_COMPONENT_LC = new Set([
  "employee code",
  "employee name",
  "end date",
]);

/**
 * Detects which CSV column holds pay-component names by matching the actual cell
 * values against the company's known components (via the discovery endpoint),
 * rather than relying on the header text alone.
 *
 * How it works (kept O(1) in file size so it scales to large imports):
 *  1. Sample only the first `DETECTION_SAMPLE_ROWS` rows.
 *  2. Collect distinct non-empty values per candidate column, skipping columns
 *     that exceed `DETECTION_MAX_DISTINCT_PER_COLUMN` (a component column is
 *     low-cardinality; employee code/name columns are not).
 *  3. Ask the discovery endpoint about those values (capped at
 *     `DETECTION_MAX_VALUES`) — known components resolve to a non-empty list.
 *  4. Pick the column whose values resolve to the most known components.
 *
 * Returns `detectedColumn: null` while loading or when no column's values match
 * any known component (the caller can then fall back to header-based detection).
 */
export function useDetectComponentColumn(
  companyCode: string,
  preview: CsvPreview | null,
  mypayEnvironment: MyPayEnvironment | null,
): { detectedColumn: string | null; isLoading: boolean } {
  const { candidates, allValues } = useMemo(() => {
    const cols: { column: string; values: string[] }[] = [];
    const all = new Set<string>();
    if (preview) {
      const sample = preview.rows.slice(0, DETECTION_SAMPLE_ROWS);
      for (const header of preview.headers) {
        if (NON_COMPONENT_LC.has(header.toLowerCase())) continue;
        const seen = new Set<string>();
        let tooMany = false;
        for (const row of sample) {
          const value = (row[header] ?? "").trim();
          if (!value) continue;
          seen.add(value);
          if (seen.size > DETECTION_MAX_DISTINCT_PER_COLUMN) {
            tooMany = true;
            break;
          }
        }
        // Skip high-cardinality (non-component) columns; keep the query small.
        if (tooMany || seen.size === 0) continue;
        const values = [...seen];
        cols.push({ column: header, values });
        for (const value of values) all.add(value);
      }
    }
    return {
      candidates: cols,
      allValues: [...all].slice(0, DETECTION_MAX_VALUES),
    };
  }, [preview]);

  const query = useComponentsParameters(companyCode, allValues, mypayEnvironment);

  const detectedColumn = useMemo(() => {
    const data = query.data;
    if (!data) return null;

    const resolved = new Set(
      Object.entries(data)
        .filter(([, params]) => params.length > 0)
        .map(([name]) => name),
    );
    if (resolved.size === 0) return null;

    let best: { column: string; score: number } | null = null;
    for (const { column, values } of candidates) {
      const score = values.filter((v) => resolved.has(v)).length;
      if (score > 0 && (best === null || score > best.score)) {
        best = { column, score };
      }
    }
    return best?.column ?? null;
  }, [query.data, candidates]);

  return { detectedColumn, isLoading: query.isLoading };
}
